import { FormHelperText, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import { ErrorOutline } from '@material-ui/icons'
import arrayMutators from 'final-form-arrays'
import { get } from 'lodash'
import { useEffect, useState } from 'react'
import {
  ArrayInput,
  FormDataConsumer,
  Identifier,
  Labeled,
  maxLength,
  minLength,
  NumberInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useDataProvider,
  useNotify,
} from 'react-admin'
import { Form, useForm } from 'react-final-form'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { ConfirmDialog } from 'shared/components/Dialog/ConfirmDialog'
import { Text } from 'shared/components/Text/Text'
import { pluralize } from 'shared/helpers/utils'
import { warningD1 } from 'shared/theme/colors'
import styled from 'styled-components'

import { BrokerStatus } from 'components/Brokers/BrokerStatusTransitions'
import { BrokerReferenceInput, BrokerReferenceInputClassName } from 'components/Common/Brokers'
import { LoadStopReferenceCodeInput } from 'components/Common/Loads'
import { AssignedDriverReferenceInput, CalloutField, DateTimeInput24hr } from 'components/Fields'
import { LoadingTypeChoices, StopTypeDetailChoices, SUPPORTED_EQUIPMENT_CHOICES } from 'helpers/constants'
import { getCountryFromCode } from 'helpers/countries'
import { validateInteger } from 'helpers/validators'
import { getStateChoices } from '../../helpers/states'
import { EquipmentType } from '../../models/equipment'

function transformPathToDeepDiffKey(path: string) {
  // This function transforms a path like "stops[0].date_start" to "root['stops'][0]['date_start']", which is the format of the keys returned in the
  // deep diff discrepancies object.
  let transformed = path.replace(/\.([^[\]]+)|^([^[\]]+)/g, "['$1$2']") // Enclose every word character sequence not enclosed by brackets with [' and ']
  return 'root' + transformed
}

interface JobDiscrepancyFormProps {
  initialValues: any
  handleSubmit: (values: any) => Promise<void>
  handleError?: (err: any) => void
  discrepancies?: any
  jobId?: Identifier
}

const LoadDetailsGridWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`

function formatDateISOString(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }
  return date.toLocaleString('en-US', options)
}

const CustomBrokerHelperText = ({ broker_id }: any) => {
  const dataProvider = useDataProvider()
  const [broker, setBroker] = useState<string | null>(null)
  useEffect(() => {
    dataProvider.getOne('brokers', { id: broker_id }).then(({ data }: any) => {
      setBroker(data.name)
    })
  }, [broker_id, dataProvider])
  return <FormHelperText style={{ color: warningD1 }}>Original value: {broker}</FormHelperText>
}

const CustomDateTimeHelperText = ({ oldValue }: any) => {
  return (
    <FormHelperText style={{ color: warningD1 }}>
      Original value: {formatDateISOString(new Date(oldValue))}
    </FormHelperText>
  )
}

const CustomStopDateTimeInputWindow = ({ source, discrepancies, ...props }: any) => {
  const form = useForm()
  const startWindowSource = `${source}.date_start`
  const endWindowSource = `${source}.date_end`
  return (
    <div>
      <Labeled label="Date start">
        <DiscrepancyInput
          discrepancies={discrepancies}
          source={startWindowSource}
          customOnChange={(value: any) => form.change(endWindowSource, value)}
          {...props}
          isDatetime
          Input={DateTimeInput24hr}
        />
      </Labeled>
      <br />
      <Labeled label="Date end">
        <DiscrepancyInput
          discrepancies={discrepancies}
          source={endWindowSource}
          {...props}
          isDatetime
          Input={DateTimeInput24hr}
        />
      </Labeled>
    </div>
  )
}

const ErrorContainer = styled.div`
  color: ${warningD1};
  font-size: 12px;
`

const DiscrepancyContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const DiscrepancyInput = ({ source, discrepancies, Input, isDatetime, ...rest }: any) => {
  const deepdiffKey = transformPathToDeepDiffKey(source)
  const discrepancy = get(discrepancies, deepdiffKey)
  const hasDiscrepancy = discrepancy && get(discrepancy, 'old_value')
  let oldValue = get(discrepancy, 'old_value')

  return (
    <DiscrepancyContainer>
      <Input source={source} {...rest} />
      {hasDiscrepancy && (
        <ErrorContainer>
          {source === 'broker' ? (
            <CustomBrokerHelperText broker_id={oldValue} />
          ) : isDatetime ? (
            <CustomDateTimeHelperText oldValue={oldValue} />
          ) : (
            <FormHelperText style={{ color: warningD1 }}>Original value: {oldValue}</FormHelperText>
          )}
        </ErrorContainer>
      )}
    </DiscrepancyContainer>
  )
}

function parseUndefinedAsStringValues(value: any) {
  // Sets the value to an empty string if it is undefined. This allows certain fields to be 'cleared' and set to an empty string.
  return value === 'undefined' ? '' : value
}

export const JobDiscrepancyForm = ({
  initialValues,
  handleSubmit,
  handleError,
  discrepancies,
  jobId,
}: JobDiscrepancyFormProps) => {
  const [stateChoices, setStateChoices] = useState<any>([])
  const [job, setJob] = useState<any>(null)
  const notify = useNotify()
  let numberOfDiscrepancies = discrepancies ? Object.keys(discrepancies).length : null
  const dataProvider = useDataProvider()

  useEffect(() => {
    const country = getCountryFromCode('US')
    setStateChoices(getStateChoices(country?.states(true)))
    if (jobId) {
      dataProvider.getOne('jobs', { id: jobId }).then(({ data }: any) => {
        setJob(data)
      })
    }
  }, [dataProvider, jobId])

  async function handleDialogConfirm(formValues: any) {
    // if the job is not power only, we should set the trailer number on the rest of the stops to be the same as the first stop
    const stops = formValues?.stops
    const trailer_number = stops[0]?.trailer_number

    for (const stop of stops.slice(1, stops?.length)) {
      stop.trailer_number = trailer_number
    }

    await handleSubmit(formValues)
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={() => {}} // onSubmit is handled by the ConfirmDialog. This is a workaround to prevent the form from submitting when the user clicks the Approve Job button, yet we still want to use the Form component to handle validation.
      mutators={{ ...arrayMutators }}
      render={(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit}>
            <FlexBox flexDirection="column" gap="24px">
              <Typography variant="h4">Approve Job</Typography>
              {numberOfDiscrepancies && (
                <CalloutField
                  icon={ErrorOutline}
                  iconProps={{ color: 'error' }}
                  label={`${numberOfDiscrepancies} rate con ${pluralize(
                    numberOfDiscrepancies,
                    'discrepancy',
                    '',
                    false
                  )} need${numberOfDiscrepancies === 1 ? 's' : ''} review`}
                  labelVariant="h6"
                />
              )}
              <Typography variant="h6">Confirm load information</Typography>
            </FlexBox>
            <LoadDetailsGridWrap>
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="source_human_id"
                label="Broker load ID"
                validate={required()}
                Input={TextInput}
              />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="broker"
                label="Broker"
                reference="brokers"
                filter={{ status__not: BrokerStatus.Invalid.id }}
                filterToQuery={(searchText: any) => ({ search: searchText })}
                containerClassName={BrokerReferenceInputClassName.load}
                validate={required()}
                showQuickCreate={false}
                Input={BrokerReferenceInput}
              />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="contact_name"
                parse={parseUndefinedAsStringValues}
                label="Load contact name"
                Input={TextInput}
              />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="contact_email"
                parse={parseUndefinedAsStringValues}
                label="Load contact email"
                Input={TextInput}
              />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="contact_phone"
                parse={parseUndefinedAsStringValues}
                label="Load Contact phone"
                Input={TextInput}
              />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="trip_rate"
                label="Confirmed rate"
                validate={required()}
                Input={NumberInput}
              />
              <AssignedDriverReferenceInput disabled />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="equipment"
                label="Equipment"
                choices={SUPPORTED_EQUIPMENT_CHOICES}
                validate={required()}
                Input={SelectInput}
              />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="truck_length_ft"
                label="Truck length (ft)"
                validate={validateInteger}
                Input={NumberInput}
              />
              <DiscrepancyInput
                discrepancies={discrepancies}
                source="truck_weight_lb"
                label="Truck weight (lb)"
                validate={validateInteger}
                Input={NumberInput}
              />
              <TextInput source="company_name" label="Shipper name" />
            </LoadDetailsGridWrap>
            <Typography variant="h6">Review stops</Typography>
            <ArrayInput source="stops">
              <SimpleFormIterator>
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="type_detail"
                  label="Type detail"
                  choices={StopTypeDetailChoices}
                  validate={required()}
                  Input={SelectInput}
                />
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="loading_type"
                  label="Loading type"
                  choices={LoadingTypeChoices}
                  Input={SelectInput}
                />
                {
                  // @ts-ignore
                  <CustomStopDateTimeInputWindow discrepancies={discrepancies} />
                }
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="location_name"
                  label="Facility name"
                  Input={TextInput}
                />
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="location_address1"
                  label="Location address 1"
                  Input={TextInput}
                />
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="location_address2"
                  label="Location address 2"
                  Input={TextInput}
                />
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="location_city"
                  label="City"
                  validate={required()}
                  Input={TextInput}
                />
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="location_state"
                  label="State"
                  choices={stateChoices}
                  validate={[required(), maxLength(2), minLength(2)]}
                  Input={SelectInput}
                />
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="location_zip"
                  label="Zip"
                  Input={TextInput}
                />
                <DiscrepancyInput
                  discrepancies={discrepancies}
                  source="notes"
                  label="Notes"
                  Input={TextInput}
                />
                <NumberInput source="truck_weight_lb" label="Truck weight (lb)" validate={validateInteger} />
                <FormDataConsumer>
                  {({ getSource }) => {
                    if (!getSource) return null

                    const isPowerOnly = job?.load?.equipment.includes(EquipmentType.POWER_ONLY)
                    const id = getSource('trailer_number')
                    const isFirstStop = id === 'stops[0].trailer_number'

                    return isPowerOnly || (!isPowerOnly && isFirstStop) ? (
                      <DiscrepancyInput
                        discrepancies={discrepancies}
                        label="Trailer number"
                        source={id}
                        Input={TextInput}
                        key={`assigned_trailer-${id}`}
                      />
                    ) : null
                  }}
                </FormDataConsumer>
                <FormDataConsumer>
                  {({ getSource }) =>
                    getSource ? (
                      <LoadStopReferenceCodeInput
                        referenceCodeSource={getSource('reference_code')}
                        isRequired={getSource('reference_code') === 'stops[0].reference_code'}
                      />
                    ) : null
                  }
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
            <ConfirmDialog
              handleConfirm={() => handleDialogConfirm(formProps.values)}
              handleError={handleError}
              dialogContent={
                <Text variant="header2" align="center">
                  Are you sure you want to approve this job?
                </Text>
              }
              confirmLabel="Approve Job"
              overrideOnClick={
                formProps.valid ? undefined : () => notify('Please fix the errors above', { type: 'error' })
              }
            >
              <Button variant="contained" color="primary" type="submit">
                Approve Job
              </Button>
            </ConfirmDialog>
          </form>
        )
      }}
    />
  )
}
